import { useMemo, useState } from 'react';
import { Card, CardBody } from '../../components/ui/Card';
import { Field, Input, Textarea, Select } from '../../components/ui/Field';
import { Slider } from '../../components/ui/Field';
import { Button } from '../../components/ui/Button';
import { TOOL_OPTIONS } from '../../lib/catalog';
import { cn } from '../../lib/utils';
import { useModelsStore, groupModelsByProvider } from '../../store/modelsStore';
import { agentsService } from '../../services/agentsService';
import { ApiError } from '../../services/apiClient';
import { toast } from '../../store/uiStore';
import { AlertTriangle, Sparkles, Undo2 } from 'lucide-react';
import type { Agent, KnowledgeBase, ToolId } from '../../types';

export function DefinitionTab({
  agent,
  patch,
  kbs,
}: {
  agent: Agent;
  patch: (p: Partial<Agent>) => void;
  kbs: KnowledgeBase[];
}) {
  const allowed = useModelsStore((s) => s.allowed);
  const labelOf = useModelsStore((s) => s.labelOf);
  const modelsLoaded = useModelsStore((s) => s.loaded);
  const allowedGroups = useMemo(() => groupModelsByProvider(allowed), [allowed]);

  // O modelo atual do agente não está liberado para o usuário (ex.: criado por outra pessoa/área).
  const currentNotAllowed = modelsLoaded && !!agent.model && !allowed.some((m) => m.id === agent.model);

  const [enhancing, setEnhancing] = useState(false);
  // Guarda o texto anterior ao aprimorar, para permitir desfazer.
  const [previousPrompt, setPreviousPrompt] = useState<string | null>(null);

  async function enhancePrompt() {
    setEnhancing(true);
    try {
      const before = agent.systemPrompt || '';
      const improved = await agentsService.enhancePrompt({
        prompt: before,
        name: agent.name,
        description: agent.description,
        model: agent.model,
        agentId: agent.id,
      });
      if (improved && improved !== before) {
        setPreviousPrompt(before);
        patch({ systemPrompt: improved });
        toast.success('Persona aprimorada.');
      } else {
        toast.info('Sem mudanças sugeridas.');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao aprimorar a persona.');
    } finally {
      setEnhancing(false);
    }
  }

  function undoEnhance() {
    if (previousPrompt === null) return;
    patch({ systemPrompt: previousPrompt });
    setPreviousPrompt(null);
    toast.info('Persona revertida.');
  }

  function toggleTool(tool: ToolId) {
    const has = agent.tools.includes(tool);
    patch({ tools: has ? agent.tools.filter((t) => t !== tool) : [...agent.tools, tool] });
  }

  // Bases anexadas (une o campo legado de vínculo único com a lista nova).
  const attachedKbIds = useMemo(() => {
    const ids = new Set(agent.knowledgeBaseIds ?? []);
    if (agent.knowledgeBaseId) ids.add(agent.knowledgeBaseId);
    return ids;
  }, [agent.knowledgeBaseIds, agent.knowledgeBaseId]);

  function toggleKb(id: string) {
    const next = new Set(attachedKbIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Migra para o formato novo e zera o legado.
    patch({ knowledgeBaseIds: Array.from(next), knowledgeBaseId: undefined });
  }

  return (
    <div className="space-y-5">
      <Card>
        <CardBody>
          <h2 className="mb-4 text-sm font-semibold text-ink">Identidade</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nome" htmlFor="name" className="mb-0">
              <Input id="name" value={agent.name} onChange={(e) => patch({ name: e.target.value })} />
            </Field>
            <Field label="Modelo" hint="liberados para você" htmlFor="model" className="mb-0">
              <Select id="model" value={agent.model} onChange={(e) => patch({ model: e.target.value })}>
                {/* Mantém o modelo atual visível mesmo se não estiver liberado. */}
                {currentNotAllowed && (
                  <option value={agent.model}>{labelOf(agent.model)} (não liberado)</option>
                )}
                {allowedGroups.map((g) => (
                  <optgroup key={g.provider} label={g.provider}>
                    {g.models.map((m) => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </optgroup>
                ))}
              </Select>
              {currentNotAllowed && (
                <p className="mt-1.5 flex items-center gap-1.5 text-xs text-danger">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Este modelo não está liberado para o seu usuário — salvar exigirá trocar para um permitido.
                </p>
              )}
            </Field>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Descrição" htmlFor="desc" className="mb-0">
              <Input id="desc" value={agent.description} onChange={(e) => patch({ description: e.target.value })} placeholder="O que este agente faz?" />
            </Field>
            <Field label="Status" hint="arquivado não pode conversar" htmlFor="status" className="mb-0">
              <Select id="status" value={agent.status ?? 'draft'} onChange={(e) => patch({ status: e.target.value as Agent['status'] })}>
                <option value="draft">Rascunho</option>
                <option value="active">Ativo</option>
                <option value="archived">Arquivado</option>
              </Select>
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <div className="mb-1.5 flex items-end justify-between gap-3">
            <span className="text-sm font-medium text-ink">
              Persona / System prompt <span className="font-normal text-slate">· instruções base do agente</span>
            </span>
            <div className="flex items-center gap-2">
              {previousPrompt !== null && (
                <Button size="sm" variant="ghost" icon={Undo2} onClick={undoEnhance} title="Voltar ao texto anterior">
                  Desfazer
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                icon={Sparkles}
                loading={enhancing}
                onClick={enhancePrompt}
                title="Reescreve e melhora a persona usando IA"
              >
                Aprimorar
              </Button>
            </div>
          </div>
          <Textarea
            id="prompt"
            rows={8}
            value={agent.systemPrompt}
            onChange={(e) => {
              // Edição manual invalida o "desfazer" do aprimoramento.
              if (previousPrompt !== null) setPreviousPrompt(null);
              patch({ systemPrompt: e.target.value });
            }}
            placeholder="Descreva o papel, objetivo e tom do agente…"
          />
          <p className="mt-1.5 text-xs text-slate">
            "Aprimorar" usa o modelo do agente para reescrever o texto atual (papel, objetivo, tom, o que fazer/evitar). Você pode editar depois.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="mb-4 text-sm font-semibold text-ink">Parâmetros do modelo</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-ink">Temperatura</span>
                <span className="rounded-md bg-cloud px-2 py-0.5 text-xs font-semibold text-ink">{agent.temperature.toFixed(1)}</span>
              </div>
              <Slider value={agent.temperature} onChange={(v) => patch({ temperature: v })} />
              <p className="mt-1.5 text-xs text-slate">0 = determinístico · 1 = criativo</p>
            </div>
            <Field label="Máx. tokens" htmlFor="maxtokens" className="mb-0">
              <Input id="maxtokens" type="number" value={agent.maxTokens} onChange={(e) => patch({ maxTokens: Number(e.target.value) })} />
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="mb-1 text-sm font-semibold text-ink">Ferramentas</h2>
          <p className="mb-4 text-xs text-slate">Capacidades que o agente pode invocar.</p>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {TOOL_OPTIONS.map((tool) => {
              const active = agent.tools.includes(tool.value);
              return (
                <button
                  key={tool.value}
                  onClick={() => toggleTool(tool.value)}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border p-3 text-left transition-colors',
                    active ? 'border-brand-orange bg-brand-orange/5' : 'border-line bg-white hover:bg-cloud',
                  )}
                >
                  <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg', active ? 'bg-brand-orange text-white' : 'bg-cloud text-slate')}>
                    <tool.icon className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-medium text-ink">{tool.label}</span>
                </button>
              );
            })}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <h2 className="mb-4 text-sm font-semibold text-ink">Conhecimento</h2>
          <Field
            label="Bases de conhecimento (opcional)"
            hint="o agente já usa os próprios arquivos; anexe bases para ampliar o contexto"
            className="mb-0"
          >
            {kbs.length === 0 ? (
              <p className="rounded-lg bg-cloud px-3 py-2 text-xs text-slate">
                Nenhuma base cadastrada. Crie em “Bases de Conhecimento”.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {kbs.map((kb) => {
                  const active = attachedKbIds.has(kb.id);
                  return (
                    <button
                      key={kb.id}
                      type="button"
                      onClick={() => toggleKb(kb.id)}
                      className={cn(
                        'flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-sm transition-colors',
                        active ? 'border-brand-orange bg-brand-orange/5 text-ink' : 'border-line bg-white text-slate hover:bg-cloud',
                      )}
                    >
                      <span className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded border', active ? 'border-brand-orange bg-brand-orange text-white' : 'border-line')}>
                        {active && <span className="text-[10px] leading-none">✓</span>}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{kb.name}</span>
                      <span className="text-[10px] text-slate">{kb.documentCount}d</span>
                    </button>
                  );
                })}
              </div>
            )}
          </Field>
        </CardBody>
      </Card>
    </div>
  );
}
